<template>
  <section
    id="waypoint"
    ref="stickyNav"
    class="ns-sticky-nav js-ns-sticky-nav"
    :class="{
      'no-cta': !ctaCopy,
      'show-shadow': message,
      'extra-padding': showExtraPadding
    }"
  >
    <div
      class="ns-sticky-nav__container"
      :class="{ 'open-drawer': drawerIsOpen }"
    >
      <!-- ---------------- -->
      <!-- NAVIGATION LINKS -->
      <!-- ---------------- -->
      <template v-if="!message">
        <!-- MOBILE TITLE AND DROPDOWN -->
        <p class="ns-sticky-nav__page-title">{{ pageTitle }}</p>
        <div
          class="ns-sticky-nav__chevron js-toggle-sticky-drawer"
          @click="toggleDrawer"
        >
          <iron-icon icon="nuskin:icon-chevron-down"></iron-icon>
        </div>
        <!-- END MOBILE TITLE AND DROPDOWN -->

        <ul class="ns-sticky-nav__link-list">
          <li
            v-for="navItem in navigation"
            :key="navItem.name"
            :class="{ active: activeLink && navItem.href === activeLink }"
          >
            <a
              :href="'#' + navItem.href"
              @click="scrollToLink"
              v-html="navItem.title"
            />
          </li>
        </ul>
      </template>

      <!-- ------- -->
      <!-- MESSAGE -->
      <!-- ------- -->
      <div v-if="message || ctaCopy" class="ns-sticky-nav__message-cta">
        <div v-if="message" class="ns-sticky-nav__message">
          {{ message }}
        </div>
        <div
          v-if="ctaCopy"
          class="ns-sticky-nav__cta"
          :class="{ 'ns-sticky-nav__cta--no-flex': message }"
        >
          <ns-button
            :href="ctaHref"
            :target="ctaOpenNewTab ? '_blank' : '_self'"
            :theme="ctaAccent ? 'primary-hit' : 'primary'"
            :size="ctaShort ? 'short' : 'adaptive-reverse'"
            >{{ ctaCopy }}</ns-button
          >
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import "waypoints/lib/noframework.waypoints.js";
import "waypoints/lib/shortcuts/sticky.min.js";

export default {
  name: "NsStickyNav",
  props: {
    /**
     * Call to action Button label
     */
    ctaCopy: {
      type: String,
      default: ""
    },
    /**
     * Call to action Button link
     */
    ctaHref: {
      type: String,
      default: ""
    },
    /**
     * Makes Call to action Button purple
     */
    ctaAccent: Boolean,
    /**
     * Call to action Button will open link in new tab
     */
    ctaOpenNewTab: Boolean,
    /**
     * Makes the button short always
     */
    ctaShort: Boolean,
    /**
     * Message text. When used, navigation will be hidden.
     */
    message: {
      type: String,
      default: ""
    },
    /**
     * Navigation links. Ignored if using 'message' prop.
     */
    navigation: {
      type: Array,
      default: function() {
        return [];
      }
    },
    /**
     * Navigation/page title
     */
    pageTitle: {
      type: String,
      default: ""
    }
  },
  data: function() {
    return {
      drawerIsOpen: false,
      activeLink: "",
      isSticky: false
    };
  },
  computed: {
    showExtraPadding() {
      return (this.ctaCopy && this.ctaShort) || (this.message && !this.ctaCopy);
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.initWaypoints();
    });
  },
  methods: {
    initWaypoints: async function() {
      // wait for sections to load
      await new Promise(resolve => setTimeout(resolve, 500));

      // make component a sticky navbar
      const el = this.$refs.stickyNav;
      const stickyHeader = new Waypoint.Sticky({
        element: el
      });

      // add waypoint to each element referenced in sticky nav
      // waypoint function will add active class to sticky nav item
      const selectors = this.navigation.map(nav => `#${nav.href}`).join(", ");

      // Only initialize navigation waypoints if there isn't a message and jumppoints exist
      if (!this.message && selectors) {
        const sections = document.querySelectorAll(selectors);
        sections.forEach(section => {
          new Waypoint({
            element: section,
            offset: `${this.$refs.stickyNav.offsetHeight}px`,
            handler: direction => {
              if (direction === "down") {
                this.activeLink = section.id;
              } else {
                this.activeLink = section.previousElementSibling
                  ? section.previousElementSibling.id
                  : "";
              }
            }
          });
        });
      }
    },
    scrollToLink(event) {
      const hash = event.target.hash;

      // close drawer on mobile
      this.drawerIsOpen = false;

      // get target by id || name attribute
      let target = document.querySelector(hash);
      target = target || document.getElementsByName(hash.slice(1))[0];

      if (target) {
        event.preventDefault();
        const docEl = document.documentElement;
        const box = target.getBoundingClientRect();
        const offsetTop = box.top + window.pageYOffset - docEl.clientTop;

        // use this instead once safari supports it
        // window.scrollTo({
        //   behavior: "smooth",
        //   left: 0,
        //   top: offsetTop - this.$refs.stickyNav.offsetHeight
        // });

        $("html, body").animate(
          {
            scrollTop: `${offsetTop - this.$refs.stickyNav.offsetHeight}px`
          },
          500
        );
      }
    },
    toggleDrawer() {
      this.drawerIsOpen = !this.drawerIsOpen;
    }
  }
};
</script>

<style lang="scss" scoped>
@import "~@nuskin/ns-core-styles/src/scss/_global.mixins";
@import "~@nuskin/ns-core-styles/src/scss/globalVariables";

.ns-sticky-nav * {
  box-sizing: border-box;
  font-family: var(--font-family-primary) !important;
}

.ns-sticky-nav {
  position: relative;
  width: 100%;
  padding: 10px 0;
  background-color: white;
  z-index: 400;
  transition: all 0.15s ease-in-out;

  &:after {
    content: "";
    position: absolute;
    left: -3000px;
    right: -3000px;
    margin: auto;
    top: 0px;
    height: 100%;
    transition: all 0.15s ease-in-out;
    z-index: -1;
  }

  &.show-shadow:after {
    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.15);
  }
}

.ns-sticky-nav__container {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  width: 100%;
  max-width: 100vw;
  padding-left: 15px;
  padding-right: 15px;
  padding: 0 15px;
}

.ns-sticky-nav__page-title {
  font-size: 16px;
  line-height: 18px;
  font-weight: 600;
  text-align: left;
  white-space: nowrap;
}

.ns-sticky-nav__chevron {
  flex: 1 0 0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 1;
}

.ns-sticky-nav.no-cta .ns-sticky-nav__chevron {
  justify-content: center;
}

.ns-sticky-nav__chevron iron-icon {
  transition: all 0.15s ease-in-out;
}

.open-drawer .ns-sticky-nav__chevron iron-icon {
  transform: rotate(-180deg);
}

.ns-sticky-nav__link-list {
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  margin: 0 -17.5px;
  max-width: 1440px;
}

.ns-sticky-nav__link-list li {
  padding: 4px 17.5px;
  color: #63666a;
  font-weight: 300;
  font-size: 15px;
  letter-spacing: 0.8px;
  line-height: 24px;
  text-transform: uppercase;
  text-decoration: none !important;
  display: inline-block;
  transition: all 0.15s ease-in-out;
}

.ns-sticky-nav__link-list a {
  position: relative;
  color: #63666a;
  line-height: 17px;
  text-decoration: none !important;
  white-space: nowrap;
}

.ns-sticky-nav__link-list li:hover a {
  color: #272525;
}

.ns-sticky-nav__link-list li a::before {
  content: "";
  position: absolute;
  left: 0;
  right: 0;
  margin: auto;
  bottom: -3px;
  width: 100%;
  height: 2px;
  background-color: #272525;
  opacity: 0;
  visibility: hidden;
  transition: all 0.15s ease-in-out;
}

.ns-sticky-nav__link-list li.active a::before {
  opacity: 1;
  visibility: visible;
}

.ns-sticky-nav__message-cta {
  flex: 1 0 0;
  display: flex;
  flex-direction: column;
  align-items: center;

  .ns-sticky-nav__message {
    margin-bottom: 10px;
  }
}

.ns-sticky-nav__message {
  color: #332f20;
  font-size: 18px;
  // line-height: 26px;
}

.ns-sticky-nav__cta {
  flex-grow: 1;
  flex-shrink: 0;
  text-align: right;
  margin: 0 auto;

  &.ns-sticky-nav__cta--no-flex {
    flex-grow: 0;
  }
}

@media (max-width: 767px) {
  .ns-sticky-nav__page-title {
    display: none;
  }
}

@media (min-width: 768px) {
  .ns-sticky-nav {
    padding: 18px 0;
  }

  .ns-sticky-nav__chevron {
    justify-content: center;
  }

  .ns-sticky-nav__message-cta {
    flex-direction: row;
    justify-content: center;

    .ns-sticky-nav__message {
      margin-bottom: 0;
    }

    .ns-sticky-nav__cta {
      margin: 0;
      margin-left: 25px;
    }
  }
}

@media (min-width: 768px) and (max-width: 1023px) {
  .ns-sticky-nav__page-title {
    flex: 1 0 0;
  }

  .ns-sticky-nav.no-cta .ns-sticky-nav__chevron {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    margin: auto;
    width: 100%;
    height: 100%;
  }
}

@media (max-width: 1023px) {
  .ns-sticky-nav__link-list {
    background-color: white;
    align-items: flex-start;
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    width: 100%;
    margin: 0;
    box-shadow: 0 4px 4px -2px rgba(0, 0, 0, 0.15);
    overflow: hidden;
    max-height: 0;
    transition: all 0.3s ease-in-out;
  }

  .ns-sticky-nav__link-list li {
    border-bottom: 1px solid #dddddd;
    width: 100%;
    padding: 0;
  }

  .ns-sticky-nav__link-list li:last-child {
    border-bottom: none;
  }

  .ns-sticky-nav__link-list a {
    display: inline-block;
    padding: 15px 10px;
    width: 100%;
    font-weight: 400;
    letter-spacing: 0;
    text-transform: none;
  }

  .ns-sticky-nav__link-list li.active a {
    font-weight: 600;
  }

  .ns-sticky-nav__link-list li a::before {
    left: 0;
    right: auto;
    top: 0;
    bottom: 0;
    height: 100%;
    width: 2px;
  }

  .open-drawer .ns-sticky-nav__link-list {
    max-height: 500px;
    overflow: auto;
  }
}

@media (min-width: 1024px) {
  .ns-sticky-nav {
    padding: 10px 0;

    &.extra-padding {
      padding: 18px 0;
    }
  }

  .ns-sticky-nav__container {
    /*max-width: 1024px;*/
    max-width: calc(1024px - 1024px / 6);
    margin-left: auto;
    margin-right: auto;
    padding: 0 15px;
  }

  .ns-sticky-nav__page-title,
  .ns-sticky-nav__chevron {
    display: none;
  }
}

@media (min-width: 1440px) {
  .ns-sticky-nav__container {
    /*max-width: 1440px;*/
    max-width: calc(1440px - 1440px / 6);
  }
}

.stuck {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;

  &:after {
    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.15);
  }
}
</style>
